import { useRef, useState } from "react";

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";

import type {
  Assignment,
  ExerciseMode,
  Organization,
  Practitioner,
  SlotTemplate,
} from "@/api/types";
import { buildDayBlocks, colorForPractitioner, type DayBlock } from "@/lib/dayBlocks";
import { slotIsOperating } from "@/lib/operatingHours";

export type BlockAction =
  /** Open the correction dialog — move, reassign, and declare the réalisé. */
  | { kind: "edit"; assignments: Assignment[] }
  | { kind: "move"; assignments: Assignment[]; newDate: string }
  | { kind: "duplicate"; assignments: Assignment[]; newDate: string }
  | { kind: "delete"; assignments: Assignment[] }
  | { kind: "copy"; assignments: Assignment[] }
  | { kind: "paste"; date: string; slotId: string }
  | {
      kind: "resize";
      assignments: Assignment[];
      practitionerId: string;
      exerciseModeId: string;
      date: string;
      newSlotIds: string[];
    };

export function PlanningDayTracks({
  date,
  isWeekend,
  slots,
  assignmentsBySlot,
  practitionerById,
  modeById,
  organization,
  canEdit,
  clipboardLabel,
  onAdd,
  onBlockAction,
  t,
}: {
  date: string;
  isWeekend: boolean;
  slots: SlotTemplate[];
  assignmentsBySlot: Map<string, Assignment[]>;
  practitionerById: Map<string, Practitioner>;
  modeById: Map<string, ExerciseMode>;
  organization: Organization | null;
  canEdit: boolean;
  /** Label of what sits in the planning clipboard, or null when empty. */
  clipboardLabel?: string | null;
  onAdd: (slot: SlotTemplate) => void;
  onBlockAction: (action: BlockAction) => void;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const theme = useTheme();
  const mode = theme.palette.mode;

  // Compute day blocks per practitioner. Each block can be rendered as a
  // single horizontal bar spanning its slots' columns.
  const blocks = buildDayBlocks(slots, assignmentsBySlot, date, practitionerById, modeById);
  // Group by practitioner so each gets its own row.
  const tracksMap = new Map<string, DayBlock[]>();
  for (const b of blocks) {
    const arr = tracksMap.get(b.practitionerId) ?? [];
    arr.push(b);
    tracksMap.set(b.practitionerId, arr);
  }
  const tracks = Array.from(tracksMap.entries())
    .map(([pid, blks]) => {
      blks.sort((a, b) => slots.indexOf(a.startSlot) - slots.indexOf(b.startSlot));
      return { pid, label: blks[0]!.practitionerLabel, blocks: blks };
    })
    .sort(
      (a, b) =>
        slots.indexOf(a.blocks[0]!.startSlot) - slots.indexOf(b.blocks[0]!.startSlot) ||
        a.label.localeCompare(b.label),
    );

  const slotCount = slots.length;
  // Per-block context menu + drag/resize state
  const [menu, setMenu] = useState<{ x: number; y: number; block: DayBlock } | null>(null);
  // Per-slot "add practitioner" context menu (right-click on an empty cell)
  const [addMenu, setAddMenu] = useState<{ x: number; y: number; slot: SlotTemplate } | null>(
    null,
  );
  const [dialog, setDialog] = useState<{
    kind: "move" | "duplicate";
    block: DayBlock;
    newDate: string;
  } | null>(null);
  const [dragging, setDragging] = useState<{
    block: DayBlock;
    kind: "move" | "resize-left" | "resize-right";
    startClientX: number;
    initialStartIdx: number;
    initialEndIdx: number;
    previewStartIdx: number;
    previewEndIdx: number;
    trackEl: HTMLElement | null;
  } | null>(null);
  const trackRefs = useRef(new Map<string, HTMLElement>());

  const cellWidth = (el: HTMLElement | null) =>
    el ? el.getBoundingClientRect().width / slotCount : 0;

  const handleResizeStart = (
    block: DayBlock,
    e: React.MouseEvent,
    kind: "resize-left" | "resize-right" | "move",
  ) => {
    if (!canEdit) return;
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    // Body mousedown should not start "move" when the click started on a
    // resize handle — we tag the handles' click via dataset and bail out.
    const target = e.target as HTMLElement;
    if (kind === "move" && target.dataset.dragHandle) return;
    const startIdx = slots.indexOf(block.startSlot);
    const endIdx = slots.indexOf(block.endSlot);
    const trackEl = trackRefs.current.get(block.practitionerId) ?? null;
    setDragging({
      block,
      kind,
      startClientX: e.clientX,
      initialStartIdx: startIdx,
      initialEndIdx: endIdx,
      previewStartIdx: startIdx,
      previewEndIdx: endIdx,
      trackEl,
    });

    const onMove = (ev: MouseEvent) => {
      setDragging((prev) => {
        if (!prev) return prev;
        const w = cellWidth(prev.trackEl);
        if (!w) return prev;
        const delta = Math.round((ev.clientX - prev.startClientX) / w);
        let s = prev.initialStartIdx;
        let n = prev.initialEndIdx;
        if (kind === "resize-left") {
          s = Math.max(0, Math.min(prev.initialEndIdx, prev.initialStartIdx + delta));
        } else if (kind === "resize-right") {
          n = Math.max(prev.initialStartIdx, Math.min(slotCount - 1, prev.initialEndIdx + delta));
        } else {
          const length = prev.initialEndIdx - prev.initialStartIdx;
          s = Math.max(0, Math.min(slotCount - 1 - length, prev.initialStartIdx + delta));
          n = s + length;
        }
        return { ...prev, previewStartIdx: s, previewEndIdx: n };
      });
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setDragging((prev) => {
        if (!prev) return null;
        if (
          prev.previewStartIdx === prev.initialStartIdx &&
          prev.previewEndIdx === prev.initialEndIdx
        ) {
          return null;
        }
        const newSlotIds = slots
          .slice(prev.previewStartIdx, prev.previewEndIdx + 1)
          .map((s) => s.id);
        const exerciseModeId = prev.block.assignments[0]?.exercise_mode_id ?? "";
        onBlockAction({
          kind: "resize",
          assignments: prev.block.assignments,
          practitionerId: prev.block.practitionerId,
          exerciseModeId,
          date,
          newSlotIds,
        });
        return null;
      });
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  /**
   * Right-click anywhere in the day cell, not just on a chip or a block.
   * The tracks are a CSS grid of equal columns, so the slot under the cursor
   * is derived from the x offset. preventDefault runs even without edit
   * rights, so the browser menu never appears over the planning.
   */
  const handleCellContextMenu = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    if (!canEdit || slots.length === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    const idx = Math.min(slots.length - 1, Math.max(0, Math.floor(ratio * slots.length)));
    setAddMenu({ x: e.clientX + 2, y: e.clientY - 6, slot: slots[idx]! });
  };

  return (
    <Box
      onContextMenu={handleCellContextMenu}
      sx={{
        p: 0,
        borderTop: 1,
        borderLeft: 1,
        borderColor: "divider",
        bgcolor: isWeekend ? "action.hover" : "transparent",
        gridColumn: `2 / span ${slotCount}`,
        minHeight: 56,
        cursor: canEdit ? "context-menu" : "default",
      }}
    >
      {/* Per-slot conformity strip */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${slotCount}, 1fr)`,
          gap: 0.25,
          p: 0.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {slots.map((s) => {
          const operating = slotIsOperating(organization, date, s);
          if (!operating) return <Box key={s.id} />;
          const list = assignmentsBySlot.get(`${date}__${s.id}`) ?? [];
          const f = list.length;
          const tgt = s.target_headcount;
          const status: "ok" | "degraded" | "ko" =
            f === 0 ? "ko" : f < tgt ? "degraded" : "ok";
          const color: "success" | "warning" | "error" =
            status === "ko" ? "error" : status === "degraded" ? "warning" : "success";
          return (
            <Tooltip
              key={s.id}
              title={t(`planning.cellStatusTooltip.${status}`, { filled: f, target: tgt })}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  cursor: canEdit ? "context-menu" : "default",
                }}
                onContextMenu={
                  canEdit
                    ? (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setAddMenu({ x: e.clientX + 2, y: e.clientY - 6, slot: s });
                      }
                    : undefined
                }
              >
                <Chip
                  size="small"
                  color={color}
                  label={tgt > 1 ? `${f}/${tgt}` : f > 0 ? "✓" : "—"}
                  sx={{
                    height: 14,
                    fontSize: 9,
                    fontWeight: 700,
                    "& .MuiChip-label": { px: 0.4 },
                  }}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {/* Tracks — one per practitioner present this day */}
      <Stack spacing={0.25} sx={{ p: 0.5 }}>
        {tracks.length === 0 && canEdit && (
          <Box
            onClick={() => onAdd(slots[0]!)}
            sx={{
              cursor: "pointer",
              minHeight: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "text.disabled",
              fontSize: 12,
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 0.5,
              "&:hover": { bgcolor: "action.hover", color: "text.secondary" },
            }}
          >
            {t("planning.dayEmpty")}
          </Box>
        )}

        {tracks.map((track) => (
          <Box
            key={track.pid}
            ref={(el: HTMLElement | null) => {
              if (el) trackRefs.current.set(track.pid, el);
            }}
            sx={{
              position: "relative",
              height: 28,
              display: "grid",
              gridTemplateColumns: `repeat(${slotCount}, 1fr)`,
            }}
          >
            {/* Background slot grid (faint dividers) */}
            {slots.map((s) => (
              <Box
                key={s.id}
                sx={{
                  borderLeft: 1,
                  borderColor: "divider",
                  opacity: 0.3,
                }}
              />
            ))}

            {track.blocks.map((blk, idx) => {
              // If currently dragging this block, use preview indices
              const isDraggingThis =
                dragging && dragging.block === blk;
              const startIdx = isDraggingThis ? dragging.previewStartIdx : slots.indexOf(blk.startSlot);
              const endIdx = isDraggingThis ? dragging.previewEndIdx : slots.indexOf(blk.endSlot);
              const color = colorForPractitioner(blk.practitionerId, mode);
              const left = (startIdx / slotCount) * 100;
              const width = ((endIdx - startIdx + 1) / slotCount) * 100;
              const startTime = slots[startIdx]?.start_time.slice(0, 5) ?? blk.startSlot.start_time.slice(0, 5);
              const endTime = slots[endIdx]?.end_time.slice(0, 5) ?? blk.endSlot.end_time.slice(0, 5);
              return (
                <Tooltip
                  key={idx}
                  title={
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
                        {blk.practitionerLabel}
                      </Typography>
                      <Typography variant="caption">
                        {startTime}–{endTime}
                        {blk.modeCode ? ` · ${blk.modeCode}` : ""}
                      </Typography>
                    </Box>
                  }
                >
                  <Box
                    onContextMenu={(e) => {
                      e.preventDefault();
                      // Stop here: the day cell also listens for contextmenu,
                      // and letting this bubble opened both menus at once.
                      e.stopPropagation();
                      if (!canEdit) return;
                      setMenu({ x: e.clientX + 2, y: e.clientY - 6, block: blk });
                    }}
                    onMouseDown={(e) => {
                      if (!canEdit) return;
                      if (e.button !== 0) return;
                      handleResizeStart(blk, e, "move");
                    }}
                    sx={{
                      position: "absolute",
                      left: `${left}%`,
                      width: `${width}%`,
                      top: 1,
                      bottom: 1,
                      bgcolor: isDraggingThis
                        ? alpha(color, mode === "dark" ? 0.5 : 0.35)
                        : mode === "dark"
                          ? alpha(color, 0.25)
                          : alpha(color, 0.18),
                      border: isDraggingThis ? "2px dashed" : "1px solid",
                      borderColor: color,
                      borderRadius: 0.5,
                      px: 0.5,
                      display: "flex",
                      alignItems: "center",
                      overflow: "hidden",
                      cursor: canEdit ? "grab" : "default",
                      userSelect: "none",
                      transition: isDraggingThis ? "none" : "left 80ms, width 80ms",
                      boxShadow: isDraggingThis
                        ? `0 0 0 2px ${alpha(color, 0.4)}`
                        : "none",
                      zIndex: isDraggingThis ? 2 : 1,
                    }}
                  >
                    {/* Left-edge resize handle */}
                    {canEdit && (
                      <Box
                        data-drag-handle="left"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          handleResizeStart(blk, e, "resize-left");
                        }}
                        sx={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: 10,
                          cursor: "ew-resize",
                          bgcolor: alpha(color, 0.2),
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            top: "30%",
                            bottom: "30%",
                            left: "50%",
                            width: 2,
                            transform: "translateX(-50%)",
                            bgcolor: color,
                            borderRadius: 1,
                          },
                          "&:hover": { bgcolor: alpha(color, 0.6) },
                        }}
                      />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        color: mode === "dark" ? alpha(color, 0.95) : color,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        flex: 1,
                        ml: 0.5,
                        mr: 0.5,
                      }}
                    >
                      {blk.practitionerLabel}
                      <Box component="span" sx={{ opacity: 0.7, ml: 0.5, fontSize: 9 }}>
                        {startTime}–{endTime}
                      </Box>
                    </Typography>
                    {/* Right-edge resize handle */}
                    {canEdit && (
                      <Box
                        data-drag-handle="right"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          handleResizeStart(blk, e, "resize-right");
                        }}
                        sx={{
                          position: "absolute",
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: 10,
                          cursor: "ew-resize",
                          bgcolor: alpha(color, 0.2),
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            top: "30%",
                            bottom: "30%",
                            left: "50%",
                            width: 2,
                            transform: "translateX(-50%)",
                            bgcolor: color,
                            borderRadius: 1,
                          },
                          "&:hover": { bgcolor: alpha(color, 0.6) },
                        }}
                      />
                    )}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        ))}
      </Stack>

      <Menu
        open={menu !== null}
        onClose={() => setMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={menu ? { top: menu.y, left: menu.x } : undefined}
      >
        <MenuItem disabled>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {menu?.block.practitionerLabel} ·{" "}
            {menu?.block.startSlot.start_time.slice(0, 5)}–
            {menu?.block.endSlot.end_time.slice(0, 5)}
          </Typography>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (!menu) return;
            onBlockAction({ kind: "edit", assignments: menu.block.assignments });
            setMenu(null);
          }}
        >
          {t("planning.blockEdit")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!menu) return;
            setDialog({ kind: "move", block: menu.block, newDate: "" });
            setMenu(null);
          }}
        >
          {t("planning.blockMove")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!menu) return;
            setDialog({ kind: "duplicate", block: menu.block, newDate: "" });
            setMenu(null);
          }}
        >
          {t("planning.blockDuplicate")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!menu) return;
            onBlockAction({ kind: "copy", assignments: menu.block.assignments });
            setMenu(null);
          }}
        >
          {t("planning.blockCopy")}
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (!menu) return;
            onBlockAction({
              kind: "delete",
              assignments: menu.block.assignments,
            });
            setMenu(null);
          }}
          sx={{ color: "error.main" }}
        >
          {t("planning.blockDelete")}
        </MenuItem>
      </Menu>

      <Menu
        open={addMenu !== null}
        onClose={() => setAddMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={addMenu ? { top: addMenu.y, left: addMenu.x } : undefined}
      >
        <MenuItem disabled>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {addMenu?.slot.label} · {addMenu?.slot.start_time.slice(0, 5)}–
            {addMenu?.slot.end_time.slice(0, 5)}
          </Typography>
        </MenuItem>
        <Divider />
        {clipboardLabel && (
          <MenuItem
            onClick={() => {
              if (!addMenu) return;
              onBlockAction({ kind: "paste", date, slotId: addMenu.slot.id });
              setAddMenu(null);
            }}
          >
            {t("planning.cellPaste", { what: clipboardLabel })}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (!addMenu) return;
            onAdd(addMenu.slot);
            setAddMenu(null);
          }}
        >
          {t("planning.addPractitioner")}
        </MenuItem>
      </Menu>

      <Dialog
        open={dialog !== null}
        onClose={() => setDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {dialog?.kind === "move"
            ? t("planning.moveDialogTitle")
            : t("planning.duplicateDialogTitle")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {dialog?.block.practitionerLabel} ·{" "}
              {dialog?.block.startSlot.start_time.slice(0, 5)}–
              {dialog?.block.endSlot.end_time.slice(0, 5)}
            </Typography>
            <TextField
              type="date"
              label={t("planning.targetDate")}
              value={dialog?.newDate ?? ""}
              onChange={(e) =>
                setDialog((d) => (d ? { ...d, newDate: e.target.value } : d))
              }
              InputLabelProps={{ shrink: true }}
              autoFocus
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            disabled={!dialog?.newDate}
            onClick={() => {
              if (!dialog) return;
              onBlockAction({
                kind: dialog.kind,
                assignments: dialog.block.assignments,
                newDate: dialog.newDate,
              });
              setDialog(null);
            }}
          >
            {dialog?.kind === "move"
              ? t("planning.blockMove")
              : t("planning.blockDuplicate")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
